import { useDroppable } from '@dnd-kit/core';

import { cn } from '@/lib/utils';

import { AppointmentCard } from './AppointmentCard';
import type { Appointment, Staff, TimeSlot, WeeklyCalendarLabels } from './types';
import { isSameDay, parseHHmm } from './utils';

interface StaffDayColumnProps {
  staff: Staff;
  date: Date;
  slots: string[];
  slotHeightPx: number;
  workingStart: string;
  appointments: Appointment[];
  conflicts: Set<string>;
  filteredIds: Set<string>;
  labels: WeeklyCalendarLabels;
  onSelectAppointment?: (a: Appointment) => void;
  onCreateAppointment?: (slot: TimeSlot) => void;
  isToday: boolean;
}

function DroppableSlot({
  slotId,
  date,
  staffId,
  startTime,
  slotHeightPx,
  onCreate,
  label,
}: {
  slotId: string;
  date: Date;
  staffId: string;
  startTime: string;
  slotHeightPx: number;
  onCreate?: (slot: TimeSlot) => void;
  label: string;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: slotId,
    data: { date, staffId, startTime },
  });
  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={() => onCreate?.({ date, staffId, startTime })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCreate?.({ date, staffId, startTime });
        }
      }}
      className={cn(
        'absolute left-0 right-0 border-b border-biz-border/60',
        'transition-colors duration-150 ease-biz-out biz-motion-safe biz-focus-ring',
        'hover:bg-biz-primary/5 cursor-pointer',
        startTime.endsWith(':00') && 'border-b-biz-border',
        isOver && 'bg-biz-primary/15 ring-2 ring-biz-primary ring-inset',
      )}
      style={{ top: 0, height: slotHeightPx, position: 'absolute' }}
      data-slot-id={slotId}
    />
  );
}

export function StaffDayColumn({
  staff,
  date,
  slots,
  slotHeightPx,
  workingStart,
  appointments,
  conflicts,
  filteredIds,
  labels,
  onSelectAppointment,
  onCreateAppointment,
  isToday,
}: StaffDayColumnProps) {
  const dayAppts = appointments.filter(
    (a) => a.staffId === staff.id && isSameDay(a.startTime, date),
  );

  const { h: wsH, m: wsM } = parseHHmm(workingStart);
  const workingStartMin = wsH * 60 + wsM;

  return (
    <div
      className={cn(
        'relative border-r border-biz-border last:border-r-0',
        isToday && 'bg-biz-primary/[0.02]',
      )}
      style={{ height: slots.length * slotHeightPx }}
      role="gridcell"
    >
      {slots.map((s, i) => (
        <div
          key={s}
          style={{
            position: 'absolute',
            top: i * slotHeightPx,
            left: 0,
            right: 0,
            height: slotHeightPx,
          }}
        >
          <DroppableSlot
            slotId={`${staff.id}|${date.toISOString().slice(0, 10)}|${s}`}
            date={date}
            staffId={staff.id}
            startTime={s}
            slotHeightPx={slotHeightPx}
            onCreate={onCreateAppointment}
            label={`${labels.emptySlotHint} — ${
              labels.dayNames[(date.getDay() + 6) % 7]
            } ${date.getDate()}, ${s}, ${staff.fullName}`}
          />
        </div>
      ))}
      {dayAppts.map((a) => {
        const startMin = a.startTime.getHours() * 60 + a.startTime.getMinutes();
        const endMin = a.endTime.getHours() * 60 + a.endTime.getMinutes();
        const offsetMin = startMin - workingStartMin;
        const top = (offsetMin / 30) * slotHeightPx;
        const height = Math.max(
          28,
          ((endMin - startMin) / 30) * slotHeightPx - 2,
        );
        return (
          <AppointmentCard
            key={a.id}
            appointment={a}
            labels={labels}
            topPx={top}
            heightPx={height}
            isConflicting={conflicts.has(a.id)}
            isDimmed={!filteredIds.has(a.id)}
            staffName={staff.fullName}
            onSelect={onSelectAppointment}
          />
        );
      })}
    </div>
  );
}
